import { useCallback, useEffect, useMemo, useState } from 'react';

import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceOfferRedemptionType,
  LoyaltyServiceServiceMode,
  useLoyaltyServiceOffersQuery,
} from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { LoyaltyOffer, appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { logger } from 'utils/logger';

import { ServiceOfferItem } from '../types';
import { mapServiceOffer } from '../utils';
import { getAppliedOfferIds, getInRestaurantOfferIds } from '../utils/applied-offer-ids';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

// The backend `ids` filter throws above 100 ids (validated before data access),
// so cap the batch and warn rather than fire a request that is rejected wholesale.
const MAX_BATCH_IDS = 100;

export interface AppliedOffersBatchVariables {
  ids: readonly string[];
  storeId?: string;
  serviceMode?: LoyaltyServiceServiceMode;
}

/**
 * The settled applied-offers batch result. It only exists once the query has
 * settled successfully for the live context, so `presentOfferIds` can never be
 * read without the `settledVariables` that qualify it (the readiness gate is
 * encoded in the type: `data` is `null` until then).
 */
export interface AppliedOffersBatchData {
  /** Applied-offer metadata keyed by `loyaltyEngineId`. */
  metadataById: Record<string, LoyaltyOffer>;
  /**
   * The `loyaltyEngineId`s returned by the settled batch response — the eviction
   * signal (present => keep, absent => evict), NOT Apollo cache existence.
   */
  presentOfferIds: Set<string>;
  /** The variables this settled response corresponds to (eviction readiness gate). */
  settledVariables: AppliedOffersBatchVariables;
}

export interface UseLoyaltyServiceAppliedOffersResult {
  /**
   * True only when the batch query has settled successfully (no network/GraphQL
   * error) for the current `ids` / `storeId` / `serviceMode`. A refetch triggered
   * by a new applied offer flips this back to false until the new response lands.
   * Mirrors `data !== null`.
   */
  ready: boolean;
  /**
   * The settled batch result, or `null` when not ready (skipped, mid-rekey, or
   * errored). Present only on a successful settle for the live context.
   */
  data: AppliedOffersBatchData | null;
  /**
   * Applied-offer metadata retained across the `network-only` loading/error/re-key
   * windows so the display seams never empty. Replaced on each settle, persisted
   * across sessions, and seedable via `retainAppliedOffer`. Display only —
   * eviction/membership must read `data.presentOfferIds`.
   */
  lastSettledMetadataById: Record<string, LoyaltyOffer>;
  /**
   * Optimistically retain a just-applied offer's metadata so the display seams
   * resolve it before its batch settles. Persisted; replaced when the next settle
   * lands.
   */
  retainAppliedOffer: (offer: LoyaltyOffer) => void;
  /** Clear retained metadata when its authenticated owner is retired. */
  clearRetainedMetadata: () => void;
  /** Force a network refetch of the applied-offer set. */
  refresh: () => void;
}

/**
 * Cold-start recovery of the applied-offer set in a single request, using the
 * `LoyaltyServiceOffers` `ids` batch filter.
 * Runs app-wide (it does not depend on the Offers screen being mounted) and
 * exposes the eviction signals the cart cleanup relies on.
 *
 * The unified query uses optional authentication: the shared auth link supplies
 * a token when a loyalty user is present and the backend returns the global feed
 * otherwise.
 */
export function useLoyaltyServiceAppliedOffers({
  enabled = true,
}: { enabled?: boolean } = {}): UseLoyaltyServiceAppliedOffersResult {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number ?? undefined;
  const { loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();

  // Sorted so the query variables are stable regardless of applied-offer /
  // cart-entry ordering, avoiding spurious refetches (and stale-read windows).
  // In-restaurant offers live only in `inRestaurantRedemptionCart`, so fold
  // their engine ids in too — otherwise an in-restaurant-only offer is never
  // batched, never lands in the metadata seam, and disappears from the cart
  // under the ON gate (OFF sees it because the full offer feed carries it).
  const ids = useMemo(() => {
    const all = [
      ...new Set([
        ...getAppliedOfferIds(appliedOffers, minimalCartEntries),
        ...getInRestaurantOfferIds(inRestaurantRedemptionCart),
      ]),
    ].sort();
    if (all.length > MAX_BATCH_IDS) {
      logger.warn({
        message: 'Loyalty-service applied-offer batch exceeded id cap; truncating',
        appliedOfferCount: all.length,
        cap: MAX_BATCH_IDS,
      });
      return all.slice(0, MAX_BATCH_IDS);
    }
    return all;
  }, [appliedOffers, minimalCartEntries, inRestaurantRedemptionCart]);

  const loyaltyServiceMode = getLoyaltyServiceServiceMode(serviceMode);

  // Never send `ids: []` — the backend treats empty/omitted ids as "return the
  // full eligible feed", which would defeat the targeted recovery.
  const baseSkip = !enabled || loyaltyUserIdLoading || !storeId || ids.length === 0;

  // `includeOfferGroups: false` — the applied set is addressed by individual
  // `loyaltyEngineId`, so we want each match back as a bare `LoyaltyServiceOffer`
  // keyed by its own id (not wrapped in a group whose top-level id is the group
  // id). Membership below therefore keys directly off `item.id` without unwrapping.
  const variables = {
    ids,
    serviceMode: loyaltyServiceMode,
    storeId,
    redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
    includeOfferGroups: false,
    omitInvalids: false,
  };

  const { data: queryData, loading, error, refetch } = useLoyaltyServiceOffersQuery({
    variables,
    skip: baseSkip,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const items = queryData?.loyaltyServiceOffers?.items;

  // Settled === ran, not loading, no network/GraphQL error, and data present.
  // A variables change (new applied offer, store, or service mode) forces a
  // network-only refetch, so `loading` is true mid-rekey and this stays false —
  // the stale-variable discard for the eviction readiness gate.
  const ready = !baseSkip && !loading && !error && !!items;

  // `data` bundles the eviction membership + metadata with the variables they
  // were settled against, and is null until ready — so a consumer cannot read
  // `presentOfferIds` without the `settledVariables` that qualify it.
  const data = useMemo<AppliedOffersBatchData | null>(() => {
    if (!ready) {
      return null;
    }
    const settledItems = (items ?? []) as ServiceOfferItem[];
    const metadataById: Record<string, LoyaltyOffer> = {};
    const presentOfferIds = new Set<string>();
    for (const item of settledItems) {
      // With `includeOfferGroups: false` the batch returns only bare offers;
      // ignore any group item defensively so membership is never keyed off a
      // group id.
      if (item.__typename !== 'LoyaltyServiceOffer') {
        continue;
      }
      // Key by `loyaltyEngineId`: that is the applied-offer id the whole result
      // contract is documented and consumed by (`ids` come from
      // `getAppliedOfferIds`, the cache policy treats it as the cart lookup key).
      // Keying by `item.id` would make `metadataById`/`presentOfferIds` miss
      // whenever the two fields differ. Fall back to `id` only if it is absent.
      const key = item.loyaltyEngineId ?? item.id;
      if (!key) {
        continue;
      }
      metadataById[key] = appOfferToLoyaltyOffer(mapServiceOffer(item));
      presentOfferIds.add(key);
    }
    return {
      metadataById,
      presentOfferIds,
      settledVariables: { ids, storeId, serviceMode: loyaltyServiceMode },
    };
  }, [ready, items, ids, storeId, loyaltyServiceMode]);

  // Retained across the `network-only` windows where `data` is null so the display
  // seams do not momentarily empty. Initialized from persisted storage so an offer
  // applied in a prior session resolves before the batch settles.
  const [lastSettledMetadataById, setLastSettledMetadataById] = useState<
    Record<string, LoyaltyOffer>
  >(
    () =>
      syncStorage.getObject<Record<string, LoyaltyOffer>>(SyncStorageKey.APPLIED_OFFER_METADATA) ??
      {}
  );

  const commitRetainedMetadata = useCallback((next: Record<string, LoyaltyOffer>): Record<
    string,
    LoyaltyOffer
  > => {
    syncStorage.setObject(SyncStorageKey.APPLIED_OFFER_METADATA, next);
    return next;
  }, []);

  // Each successful settle replaces retention with its metadata, so a removed or
  // expired offer drops out. A null `data` (loading/error/re-key) leaves retention
  // untouched — except terminal-empty, where the batch is skipped and never settles.
  useEffect(() => {
    if (data) {
      setLastSettledMetadataById(() => commitRetainedMetadata(data.metadataById));
      return;
    }
    // Removing the last offer skips the batch (`ids: []`), so clear retention here or
    // the seam keeps resolving the deleted offer during cart cleanup. Gated like
    // `baseSkip` to exclude transient windows; the empty-map short-circuit avoids
    // wiping the cold-start seed (ids not yet hydrated) and storage thrash.
    const liveSetEmpty = enabled && !loyaltyUserIdLoading && !!storeId && ids.length === 0;
    if (!liveSetEmpty) {
      return;
    }
    setLastSettledMetadataById(prev =>
      Object.keys(prev).length === 0 ? prev : commitRetainedMetadata({})
    );
  }, [data, commitRetainedMetadata, enabled, loyaltyUserIdLoading, storeId, ids.length]);

  const retainAppliedOffer = useCallback(
    (offer: LoyaltyOffer) => {
      const key = offer.loyaltyEngineId;
      if (!key) {
        return;
      }
      setLastSettledMetadataById(prev => commitRetainedMetadata({ ...prev, [key]: offer }));
    },
    [commitRetainedMetadata]
  );

  const clearRetainedMetadata = useCallback(() => {
    setLastSettledMetadataById(previous =>
      Object.keys(previous).length === 0 ? previous : commitRetainedMetadata({})
    );
  }, [commitRetainedMetadata]);

  const refresh = useCallback(() => {
    if (baseSkip) {
      return;
    }
    void refetch();
  }, [baseSkip, refetch]);

  return {
    ready,
    data,
    lastSettledMetadataById,
    retainAppliedOffer,
    clearRetainedMetadata,
    refresh,
  };
}
